import React, { useLayoutEffect, useRef, useState } from "react"

import { ResultGrid } from "@app/scripts/components/result-grid"
import { ResultFooter } from "@app/scripts/components/result-footer"
import { SearchBanner } from "@app/scripts/components/search-banner"
import { FilterSortDrawer } from "@app/scripts/components/filter-sort-drawer"
import { useSearchspringSearchContext } from "@app/scripts/providers/searchspring"
import { SearchNoResults } from "@app/scripts/components/search-no-results"
import {
  CollectionRecommendationCarousel,
  type RecommendationCarouselConfig,
} from "@app/scripts/components/collection-recommendation-carousel"
import { trackRedirect } from "@app/scripts/lib/beacon"

export type Props = {
  query: string
  /** From the main-search section settings; null when switched off. */
  noResultsRecommendations?: RecommendationCarouselConfig | null
}

// Shopper intent wins: a filtered or paginated view means they are working
// through the results, so the redirect is not what they asked for.
function qualifiesForRedirect(
  response: ReturnType<typeof useSearchspringSearchContext>["response"]
) {
  if (!response?.merchandising?.redirect) return false

  const params = new URLSearchParams(window.location.search)
  if (params.get("filters")) return false
  if (Number(params.get("page")) > 1) return false

  return true
}

export function SearchGrid({ query, noResultsRecommendations = null }: Props) {
  const [searchTerm, setSearchTerm] = useState(query)
  const { response, refine } = useSearchspringSearchContext()

  const hasResponse = !!response
  const totalResults = response?.pagination?.totalResults ?? 0

  // Only the first response of a page load is eligible for a redirect. Later
  // responses come from refines the shopper made deliberately, and redirecting
  // out of results they are browsing would trap them.
  const redirectEvaluatedRef = useRef(false)
  const redirectingRef = useRef(false)

  // Layout effect so the navigation is issued before the browser paints the
  // zero-results frame - the shopper should never glimpse "No results" for a
  // term that redirects.
  useLayoutEffect(() => {
    if (!response || redirectEvaluatedRef.current) return
    redirectEvaluatedRef.current = true

    if (!qualifiesForRedirect(response)) return

    redirectingRef.current = true
    // Beacon has to be dispatched before navigation; its sendBeacon/keepalive
    // transport survives the unload, so there is nothing to await.
    trackRedirect(
      "search",
      response.responseId ?? "",
      response.merchandising.redirect
    )
    // replace() keeps the interim search page out of history so Back does not
    // bounce the shopper straight back into the redirect.
    window.location.replace(response.merchandising.redirect)
  }, [response])

  // While the redirect is pending (or the navigation is in flight), keep the
  // page in its loading state: mounting SearchBanner would swap the skeleton
  // for a "No results" banner in the same frame the redirect fires.
  const isRedirecting =
    (!redirectEvaluatedRef.current || redirectingRef.current) &&
    qualifiesForRedirect(response)

  const showNoResults = hasResponse && !isRedirecting && totalResults === 0

  return (
    <>
      {hasResponse && !isRedirecting && (
        <SearchBanner
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          refine={refine}
          response={response}
        />
      )}

      <FilterSortDrawer />

      <ResultGrid />

      {showNoResults && noResultsRecommendations && (
        <CollectionRecommendationCarousel config={noResultsRecommendations} />
      )}

      {showNoResults && <SearchNoResults searchPage={true} />}

      {hasResponse && totalResults > 0 && <ResultFooter />}
    </>
  )
}
